/**
 * Emerald Books — Budgets & Variance.
 *
 *   • Create a fiscal-year budget with per-account monthly amounts
 *   • Delete
 *   • Drill-down: budget vs actual variance across 12 months, filterable
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, BarChart2, Search, XCircle, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { booksClient, unwrapList, errMsg } from './api';

const money = (n) => (Number(n) || 0).toFixed(2);
const MONTHS_CAL = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** Rotate calendar months so index 0 lines up with the fiscal-year start month.
 *  e.g. fy_start_month=4 (April) → ['Apr','May','Jun',...,'Mar'].
 *  Parses the month directly from the ISO string to avoid local-TZ shifts. */
const fyMonthLabels = (fyStartIso) => {
  const monthNum = fyStartIso && /^\d{4}-\d{2}/.test(fyStartIso)
    ? Number(fyStartIso.slice(5, 7)) - 1
    : 3;
  return Array.from({ length: 12 }, (_, i) => MONTHS_CAL[(monthNum + i) % 12]);
};

const emptyLine = () => ({ account_id: '', monthly_amounts: Array(12).fill(0) });

const BudgetsListAndCreate = ({ onOpen }) => {
  const [budgets, setBudgets] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    label: '', fy_start: `${new Date().getFullYear()}-04-01`,
    fy_end: `${new Date().getFullYear() + 1}-03-31`, lines: [emptyLine()],
  });
  const MONTHS = useMemo(() => fyMonthLabels(form.fy_start), [form.fy_start]);

  const load = async () => {
    setLoading(true);
    try {
      const [b, a] = await Promise.all([
        booksClient.get('/budgets'),
        booksClient.get('/ledger/accounts'),
      ]);
      setBudgets(b.data); setAccounts(unwrapList(a));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const opRexAccounts = useMemo(() => accounts.filter((a) => ['income', 'expense'].includes(a.type)), [accounts]);

  const updateLine = (i, patch) => setForm((f) => ({ ...f, lines: f.lines.map((l, idx) => idx === i ? { ...l, ...patch } : l) }));
  const updateMonth = (li, mi, v) => setForm((f) => ({
    ...f,
    lines: f.lines.map((l, idx) => idx === li ? { ...l, monthly_amounts: l.monthly_amounts.map((x, j) => j === mi ? (parseFloat(v) || 0) : x) } : l),
  }));
  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, emptyLine()] }));
  const removeLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));
  const distributeEvenly = (li, total) => {
    const monthly = (parseFloat(total) || 0) / 12;
    setForm((f) => ({
      ...f,
      lines: f.lines.map((l, idx) => idx === li ? { ...l, monthly_amounts: Array(12).fill(Number(monthly.toFixed(2))) } : l),
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const lines = form.lines.filter((l) => l.account_id);
    if (!lines.length) { toast.error('Add at least one account line'); return; }
    try {
      await booksClient.post('/budgets', { ...form, lines });
      toast.success('Budget created');
      setShowForm(false);
      setForm({ ...form, label: '', lines: [emptyLine()] });
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const del = async (b) => {
    if (!confirm(`Delete budget "${b.label}"?`)) return;
    try { await booksClient.delete(`/budgets/${b.budget_id}`); toast.success('Deleted'); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="mx-auto max-w-6xl" data-testid="books-budgets-page">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Budgets & Variance</h1>
          <p className="mt-1 text-sm text-slate-500">Set monthly targets per income/expense account, then track how you're pacing.</p>
        </div>
        <Button onClick={() => setShowForm(!showForm)} className="bg-emerald-600 text-white hover:bg-emerald-500" data-testid="budget-new-btn">
          <Plus className="mr-1 h-4 w-4" /> New budget
        </Button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="mb-6 rounded-xl border bg-white p-5 shadow-sm" data-testid="budget-form">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            <label className="text-sm">
              <div className="mb-1 text-xs text-slate-500">Label</div>
              <Input required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="FY 2026-27" data-testid="budget-label" />
            </label>
            <label className="text-sm">
              <div className="mb-1 text-xs text-slate-500">FY start</div>
              <Input type="date" required value={form.fy_start} onChange={(e) => setForm({ ...form, fy_start: e.target.value })} />
            </label>
            <label className="text-sm">
              <div className="mb-1 text-xs text-slate-500">FY end</div>
              <Input type="date" required value={form.fy_end} onChange={(e) => setForm({ ...form, fy_end: e.target.value })} />
            </label>
          </div>

          <div className="mt-4">
            <div className="mb-2 text-xs font-medium text-slate-600">Lines (per account, monthly amounts)</div>
            <div className="space-y-4">
              {form.lines.map((l, li) => (
                <div key={li} className="rounded border bg-slate-50 p-3">
                  <div className="mb-2 flex items-center gap-2">
                    <select className="flex-1 rounded-md border px-2 py-1.5 text-sm bg-white" value={l.account_id} onChange={(e) => updateLine(li, { account_id: e.target.value })} data-testid={`budget-line-account-${li}`}>
                      <option value="">Select income/expense account…</option>
                      {opRexAccounts.map((a) => <option key={a.account_id} value={a.account_id}>{a.type === 'income' ? '📈' : '📉'} {a.code} · {a.name}</option>)}
                    </select>
                    <Input className="w-32" type="number" step="0.01" placeholder="Total ÷ 12" onChange={(e) => distributeEvenly(li, e.target.value)} title="Auto-distribute across 12 months" data-testid={`budget-line-total-${li}`} />
                    <button type="button" onClick={() => removeLine(li)} className="text-rose-600 hover:text-rose-700 disabled:opacity-30" disabled={form.lines.length === 1}><Trash2 className="h-4 w-4" /></button>
                  </div>
                  <div className="grid grid-cols-6 gap-2 md:grid-cols-12">
                    {l.monthly_amounts.map((v, mi) => (
                      <label key={mi} className="text-xs">
                        <div className="text-slate-500">{MONTHS[mi]}</div>
                        <Input type="number" step="0.01" value={v} onChange={(e) => updateMonth(li, mi, e.target.value)} className="px-1 py-1 text-xs" data-testid={`budget-line-${li}-m${mi}`} />
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <button type="button" onClick={addLine} className="mt-2 text-sm text-emerald-600 hover:underline" data-testid="budget-add-line">+ Add account line</button>
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button type="submit" className="bg-emerald-600 text-white hover:bg-emerald-500" data-testid="budget-save">Save budget</Button>
          </div>
        </form>
      )}

      <div className="space-y-3">
        {loading && <div className="rounded-xl border bg-white p-6 text-slate-400"><Loader2 className="inline h-4 w-4 animate-spin" /> Loading…</div>}
        {!loading && budgets.length === 0 && <div className="rounded-xl border bg-white p-10 text-center text-slate-400">No budgets yet.</div>}
        {budgets.map((b) => (
          <div key={b.budget_id} className="flex items-center justify-between rounded-xl border bg-white p-4 shadow-sm" data-testid={`budget-${b.budget_id}`}>
            <div>
              <div className="font-medium">{b.label}</div>
              <div className="text-xs text-slate-500">{b.fy_start} → {b.fy_end} · {b.lines?.length || 0} account{(b.lines?.length || 0) !== 1 ? 's' : ''}</div>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => onOpen(b)} data-testid={`budget-variance-${b.budget_id}`}>
                <BarChart2 className="mr-1 h-3 w-3" /> Variance
              </Button>
              <button onClick={() => del(b)} className="rounded p-1 text-slate-500 hover:text-rose-600" title="Delete" data-testid={`budget-delete-${b.budget_id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const VarianceView = ({ budget, onBack }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(0);
  const MONTHS = useMemo(() => fyMonthLabels(budget.fy_start), [budget.fy_start]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await booksClient.get(`/budgets/${budget.budget_id}/variance`, { params: month ? { month } : {} });
      setRows(data.rows || []);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [month]);

  const totals = useMemo(() => rows.reduce((acc, r) => ({
    budget: acc.budget + r.budget, actual: acc.actual + r.actual, variance: acc.variance + r.variance,
  }), { budget: 0, actual: 0, variance: 0 }), [rows]);

  return (
    <div className="mx-auto max-w-6xl" data-testid="budget-variance-view">
      <button onClick={onBack} className="mb-4 flex items-center gap-1 text-sm text-slate-500 hover:underline" data-testid="budget-variance-back"><ArrowLeft className="h-3 w-3" /> Back to budgets</button>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Variance · {budget.label}</h1>
          <p className="mt-1 text-sm text-slate-500">{budget.fy_start} → {budget.fy_end}</p>
        </div>
        <select value={month} onChange={(e) => setMonth(parseInt(e.target.value, 10))} className="rounded-md border px-2 py-1.5 text-sm" data-testid="budget-variance-month">
          <option value={0}>All months</option>
          {MONTHS.map((m, i) => <option key={i} value={i + 1}>Month {i + 1} ({m})</option>)}
        </select>
      </div>

      <div className="mb-4 grid grid-cols-3 gap-3">
        <div className="rounded-xl border bg-white p-4"><div className="text-xs text-slate-500">Budget</div><div className="text-2xl font-semibold">{money(totals.budget)}</div></div>
        <div className="rounded-xl border bg-white p-4"><div className="text-xs text-slate-500">Actual</div><div className="text-2xl font-semibold">{money(totals.actual)}</div></div>
        <div className={'rounded-xl border p-4 ' + (totals.variance >= 0 ? 'border-emerald-200 bg-emerald-50' : 'border-rose-200 bg-rose-50')}>
          <div className="text-xs text-slate-500">Variance</div>
          <div className={'text-2xl font-semibold ' + (totals.variance >= 0 ? 'text-emerald-700' : 'text-rose-700')}>{money(totals.variance)}</div>
        </div>
      </div>

      <div className="rounded-xl border bg-white">
        {loading ? <div className="p-6 text-slate-400"><Loader2 className="inline h-4 w-4 animate-spin" /> Loading…</div> :
          rows.length === 0 ? <div className="p-10 text-center text-slate-400">No data.</div> :
          <table className="w-full text-sm">
            <thead className="border-b bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="p-3 text-left">Account</th>
                <th className="p-3 text-right">Month</th>
                <th className="p-3 text-right">Budget</th>
                <th className="p-3 text-right">Actual</th>
                <th className="p-3 text-right">Variance</th>
                <th className="p-3 text-right">%</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b last:border-b-0">
                  <td className="p-3"><span className="font-mono text-xs text-slate-500 mr-1">{r.account_code}</span>{r.account_name}</td>
                  <td className="p-3 text-right">{MONTHS[r.month_index - 1]}</td>
                  <td className="p-3 text-right">{money(r.budget)}</td>
                  <td className="p-3 text-right">{money(r.actual)}</td>
                  <td className={'p-3 text-right font-medium ' + (r.variance >= 0 ? 'text-emerald-700' : 'text-rose-700')}>{money(r.variance)}</td>
                  <td className={'p-3 text-right text-xs ' + (r.variance >= 0 ? 'text-emerald-600' : 'text-rose-600')}>{Number(r.variance_pct).toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        }
      </div>
    </div>
  );
};

const BudgetsPage = () => {
  const [open, setOpen] = useState(null);
  if (open) return <VarianceView budget={open} onBack={() => setOpen(null)} />;
  return <BudgetsListAndCreate onOpen={setOpen} />;
};

export default BudgetsPage;
